// Shell do admin (`spec/17-frontend.md` §8.2) no design Zenith (WEB-100). Mesmo `AppShell`
// do seller, sem "Recebido no mês" (`spec/24` §7) e sem `ModeToggle` (o admin filtra por
// tela, não globalmente, o `PageHeader` do kit já esconde o toggle na superfície admin).
//
// Ordem dos gates igual à do servidor (`apps/api/src/middlewares/auth-session.ts`):
// sessão → troca de senha → 2FA. As duas telas de gate vivem sob este layout, então
// ficam de fora do redirect, senão cada uma se redirecionaria para si mesma.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createFileRoute,
  Navigate,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { KeyRound, ShieldCheck } from 'lucide-react';
import { api } from '../api/client.ts';
import { adminBadges, adminCountersQueryOptions } from '../api/queries/shell.ts';
import { sessionQueryOptions } from '../api/session.ts';
import { adminNav } from '../app/nav-admin.ts';
import type { AccountLink } from '../components/app/shell/conta-menu.tsx';
import { AppShell } from '../components/app/shell/shell.tsx';
import { CardSkeleton } from '../components/app/skeletons.tsx';

const CHANGE_PASSWORD_PATH = '/admin/change-password';
const SETUP_2FA_PATH = '/admin/setup-2fa';

/** Atalhos da conta do admin: as duas telas que não estão na nav. */
const ACCOUNT_LINKS: AccountLink[] = [
  { label: 'Trocar senha', to: CHANGE_PASSWORD_PATH, icon: KeyRound },
  { label: 'Configurar 2FA', to: SETUP_2FA_PATH, icon: ShieldCheck },
];

const MOBILE_TABS = ['overview', 'alerts', 'sellers', 'withdrawals'];

function AdminShell() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const session = useQuery(sessionQueryOptions);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // `enabled`: TODA rota admin (`authSession`, surface admin) devolve
  // `password_change_required`/`mfa_required` até essas duas pendências fecharem, sem
  // este gate a query bateria a cada 30s nas telas `/admin/change-password` e
  // `/admin/setup-2fa` só pra levar 403 e re-navegar pra própria tela. Erro aqui não
  // quebra o shell (WEB-50B item 4): sem badge, sem toast.
  const counters = useQuery({
    ...adminCountersQueryOptions(),
    enabled:
      session.data?.role === 'admin' &&
      !session.data.mustChangePassword &&
      session.data.twoFactorEnabled,
  });
  const signOut = useMutation({
    mutationFn: async () => api('/v1/me/auth/sign-out', { method: 'POST' }),
    onSettled: () => {
      queryClient.clear();
      void navigate({ to: '/login' });
    },
  });
  const badges = counters.data === undefined ? undefined : adminBadges(counters.data);

  if (session.isPending) {
    return (
      <div className="min-h-dvh p-4 sm:p-8">
        <CardSkeleton />
      </div>
    );
  }
  if (session.data === null || session.data === undefined) {
    return <Navigate to="/login" search={{ next: pathname }} replace />;
  }
  if (session.data.role !== 'admin') {
    return <Navigate to="/no-access" replace />;
  }
  if (session.data.mustChangePassword && pathname !== CHANGE_PASSWORD_PATH) {
    return <Navigate to={CHANGE_PASSWORD_PATH} replace />;
  }
  if (
    !session.data.twoFactorEnabled &&
    pathname !== SETUP_2FA_PATH &&
    pathname !== CHANGE_PASSWORD_PATH
  ) {
    return <Navigate to={SETUP_2FA_PATH} replace />;
  }

  const user = session.data;

  return (
    <AppShell
      groups={adminNav}
      activePath={pathname}
      subtitle="Backoffice"
      badges={badges}
      user={{ name: user.name, email: user.email, subtitle: 'Administrador' }}
      accountLinks={ACCOUNT_LINKS}
      onLogout={() => {
        signOut.mutate();
      }}
      mobileTabs={MOBILE_TABS}
    >
      <Outlet />
    </AppShell>
  );
}

export const Route = createFileRoute('/admin')({ component: AdminShell });
